import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mountain, Tent, Activity, Star, Quote, ChevronLeft, ChevronRight, House } from 'lucide-react';
import { Button } from '../components/ui/button';
import { googleReviews, tripadvisorReviews } from '../data/mock';

const Home = () => {
  const carouselRef = useRef(null);

  const scrollLeft = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({
        left: -400,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({
        left: 400,
        behavior: 'smooth'
      });
    }
  };

  const poles = [
    {
      id: 'draille',
      title: 'Gîte d\'étape et de séjour',
      subtitle: 'La Draille',
      description: 'Hébergement de groupes, familles ou individuels, pour des étapes ou séjours',
      icon: House,
      image: "gite.jpg",
      link: '/hebergements/gite-la-draille',
      color: 'from-green-900/80 to-green-700/80'
    },
    {
      id: 'fremyc',
      title: 'Activités de pleine nature',
      subtitle: 'Fremyc',
      description: 'Trottinette tout terrain, VTT, spéléologie, canyoning, escalade, ... Des aventures pour tous les niveaux',
      icon: Mountain,
      image: 'trottinette.jpg',
      link: '/activites',
      color: 'from-emerald-900/80 to-emerald-700/80'
    },
    {
      id: 'pegue',
      title: 'Camping & Base de loisirs',
      subtitle: 'La Pègue',
      description: 'Accueil de groupes de jeunes avec activités sportives et culturelles',
      icon: Tent,
      image: 'city.jpg',
      link: '/hebergements/camping-la-pegue',
      color: 'from-teal-900/80 to-teal-700/80'
    }
  ];

  // On combine les avis
  const allReviews = [...googleReviews.reviews, ...googleReviews.reviews];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/paysage.jpg')" }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
        </div>
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">Nature Cévennes</h1>
          <p className="text-xl md:text-2xl mb-12 text-gray-200 max-w-3xl mx-auto leading-relaxed">
            Optez pour le charme du <b>Gîte La Draille</b>, l'aventure du <b>Camping La Pègue</b>, ou l'adrénaline avec les activités de pleine nature proposées par <b>Fremyc</b>.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/activites">
                <Button aria-label="Découvrez nos activités" size="lg" className="bg-[#afab53] min-h-[48px] hover:bg-[#9a9648] text-white text-lg px-8 py-6 w-full">
                Découvrir nos activités
                </Button>
            </Link>
            <Link to="/contact">
              <Button aria-label="Nous contacter" size="lg" variant="outline" className="border-white min-h-[48px] text-white hover:bg-white hover:text-black text-lg px-8 py-6 w-full">
                Nous contacter
              </Button>
            </Link>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Poles Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">Choisissez votre expérience</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Optez pour le charme du <b>Gîte La Draille</b>, l'aventure du <b>Camping La Pègue</b>, ou l'adrénaline avec les activités de pleine nature proposées par <b>Fremyc</b>.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {poles.map((pole) => {
              const IconComponent = pole.icon;
              return (
                <Link 
                  key={pole.id}
                  to={pole.link}
                  className="group relative h-[500px] rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105"
                >
                  <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{ backgroundImage: `url(${pole.image})` }}></div>
                  <div className={`absolute inset-0 bg-gradient-to-t ${pole.color} transition-opacity duration-500 group-hover:opacity-90`}></div>
                  <div className="relative h-full flex flex-col justify-end p-8 text-white">
                    <div className="mb-4 transform transition-transform duration-500 group-hover:-translate-y-2">
                      <IconComponent className="w-16 h-16 mb-4" strokeWidth={1.5} />
                      <h3 className="text-3xl font-bold mb-2">{pole.subtitle}</h3>
                      <p className="text-lg text-gray-200 mb-1">{pole.title}</p>
                      <p className="text-sm text-gray-300 leading-relaxed">{pole.description}</p>
                    </div>
                    <div className="flex items-center gap-2 text-white font-semibold group-hover:gap-4 transition-all duration-300">
                      <span>Découvrir</span>
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Territory Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">Où sommes-nous ?</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Meyrueis se situe au sud de la Lozère, au confluent de trois vallées, au pied du Mont Aigoual (1567m) et au cœur du Parc National des Cévennes.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3"><span className="text-[#afab53] text-2xl">•</span><span>Gorges du Tarn, de la Jonte et de la Dourbie</span></li>
                <li className="flex items-start gap-3"><span className="text-[#afab53] text-2xl">•</span><span>Mont Aigoual et Grands Causses</span></li>
                <li className="flex items-start gap-3"><span className="text-[#afab53] text-2xl">•</span><span>Grottes et Abîmes remarquables</span></li>
              </ul>
            </div>
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img src="meyrueis.png" alt="Paysage des Cévennes" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Rating Section - RE-INSERTING LOGOS HERE */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">Ils ont vécu l'expérience Nature Cévennes</h2>
            
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 mt-10">
              {/* Google Summary Card */}
              <div className="flex flex-col items-center bg-white rounded-xl p-6 shadow-sm border border-gray-100 min-w-[280px]">
                <div className="flex items-center gap-3 mb-3">
                  <img 
                    src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" 
                    alt="Google" 
                    className="h-6"
                  />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-4xl font-bold text-[#afab53]">{googleReviews.averageRating}</span>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-6 h-6 ${i < Math.floor(googleReviews.averageRating) ? 'fill-[#afab53] text-[#afab53]' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-4">{googleReviews.totalReviews} avis</p>
                <a 
                  href={googleReviews.googlePlaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-[#afab53] text-[#afab53] rounded-lg text-sm font-semibold hover:bg-[#afab53] hover:text-white transition-colors"
                >
                  Voir les avis
                </a>
              </div>

              {/* TripAdvisor Summary Card */}
              <div className="flex flex-col items-center bg-white rounded-xl p-6 shadow-sm border border-gray-100 min-w-[280px]">
                <img 
                    src="/TRIP_BIG.png" 
                    alt="Google" 
                    className="h-6"
                  />
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-4xl font-bold text-[#afab53]">{tripadvisorReviews.averageRating}</span>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-6 h-6 ${i < Math.floor(tripadvisorReviews.averageRating) ? 'fill-[#afab53] text-[#afab53]' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-4">{tripadvisorReviews.totalReviews} avis</p>
                <a 
                  href={tripadvisorReviews.tripadvisorUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-[#afab53] text-[#afab53] rounded-lg text-sm font-semibold hover:bg-[#afab53] hover:text-white transition-colors"
                >
                  Voir les avis
                </a>
              </div>
            </div>
          </div>

          {/* Carousel Section */}
          <div className="relative mt-16">
            <button onClick={scrollLeft} aria-label="Faire défiler les avis vers la gauche" className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/90 shadow-xl rounded-full flex items-center justify-center text-[#afab53] hover:bg-[#afab53] hover:text-white transition-all border border-gray-200">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button onClick={scrollRight} aria-label="Faire défiler les avis vers la droite" className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/90 shadow-xl rounded-full flex items-center justify-center text-[#afab53] hover:bg-[#afab53] hover:text-white transition-all border border-gray-200">
              <ChevronRight className="w-6 h-6" />
            </button>

            <div ref={carouselRef} className="overflow-x-auto scrollbar-hide flex">
              <div className="animate-infinite-scroll flex gap-6 py-4">
                {[...allReviews, ...allReviews, ...allReviews].map((review, index) => (
                  <div key={index} className="w-[350px] md:w-[400px] flex-shrink-0 bg-white rounded-2xl p-8 shadow-md border border-gray-100 flex flex-col">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 bg-[#afab53] rounded-full flex items-center justify-center text-white font-bold text-lg">{review.author[0]}</div>
                      <div>
                        <h4 className="font-bold text-gray-900">{review.author}</h4>
                        <p className="text-sm text-gray-500">{review.date}</p>
                      </div>
                      <Quote className="ml-auto w-8 h-8 text-gray-100" />
                    </div>
                    <div className="flex mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-[#afab53] text-[#afab53]' : 'text-gray-200'}`} />
                      ))}
                    </div>
                    <p className="text-gray-700 italic leading-relaxed line-clamp-4 flex-grow">"{review.text}"</p>
                    
                    {/* Platform Badge Inside Card */}
                    <div className="mt-6 pt-6 border-t border-gray-100 flex items-center gap-2">
                      {review.platform === 'google' ? (
                        <>
                          <img src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" alt="Google" className="h-4 opacity-60" />
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Avis Google</span>
                        </>
                      ) : (
                        <>
                          <img src="/TRIP.png" alt="Google" className="h-4 opacity-60" />
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Avis Tripadvisor</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { label: "Années d'expérience", val: "30+" },
              { label: "Clients satisfaits", val: "5000+" },
              { label: "Activités proposées", val: "15+" },
              { label: "Sécurité garantie", val: "100%" }
            ].map((stat, i) => (
              <div key={i} className="text-center border-l border-gray-200 first:border-0">
                <div className="text-3xl font-bold text-[#afab53] mb-1">{stat.val}</div>
                <div className="text-sm text-gray-500 uppercase tracking-wide">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-gradient-to-br from-[#afab53] to-[#8a8742]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-8">Prêt pour l'aventure ?</h2>
          <p className="text-xl mb-12 text-white/90 leading-relaxed">
            Composez votre séjour sur mesure en combinant hébergement et activités. Notre équipe vous accompagne dans l'organisation de votre projet.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/sejours">
              <Button aria-label="Découvrez nos séjours" size="lg" className="bg-white text-[#afab53] hover:bg-gray-100 text-lg px-10 py-7 shadow-xl w-full sm:w-auto">
                Découvrez nos offres de séjour
              </Button>
            </Link>
            <Link to="/contact">
              <Button aria-label="Nous contacter" size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg px-10 py-7 w-full sm:w-auto">
                Nous contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;